/**
 * Phase 3.42 — Admin-Übersicht für Pfand/Refi-Anträge.
 *
 * Listet alle Pawn-Requests, erlaubt Status-Wechsel + Admin-Notiz.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Coins, Filter, Loader2, ArrowLeft, Save, Landmark } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["submitted", "reviewing", "approved", "active", "released", "rejected"];
const STATUS_LABEL = {
    submitted: "Eingereicht",
    reviewing: "In Prüfung",
    approved: "Genehmigt",
    active: "Aktiv",
    released: "Freigegeben",
    rejected: "Abgelehnt",
};
const STATUS_CLS = {
    submitted: "bg-blue-100 text-blue-800",
    reviewing: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-800",
    active: "bg-violet-100 text-violet-800",
    released: "bg-gray-200 text-gray-700",
    rejected: "bg-red-100 text-red-800",
};
const TYPE_LABEL = {
    b2c_pawn: "B2C-Pfand",
    b2b_pawn_quick: "B2B Einkaufsfinanzierung",
    b2b_refi: "B2B Nebensaison-Refi",
};

export default function AdminPawnPage() {
    const [items, setItems] = useState(null);
    const [filter, setFilter] = useState("");
    const [escrow, setEscrow] = useState([]);
    const [drafts, setDrafts] = useState({});
    const [savingId, setSavingId] = useState(null);

    const load = async () => {
        try {
            const q = filter ? `?status=${filter}` : "";
            const r = await api.get(`/admin/finance/pawn-requests${q}`);
            setItems(r.data.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler beim Laden");
        }
    };
    const loadEscrow = async () => {
        try {
            const r = await api.get("/admin/finance/escrow");
            setEscrow(r.data.items || []);
        } catch { /* ignore */ }
    };
    useEffect(() => { load(); loadEscrow(); }, [filter]);

    const update = async (id, status, admin_note) => {
        setSavingId(id);
        try {
            await api.patch(`/admin/finance/pawn-requests/${id}/status`, { status, admin_note });
            toast.success("Status aktualisiert");
            setDrafts((p) => ({ ...p, [id]: undefined }));
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler");
        } finally {
            setSavingId(null);
        }
    };

    if (items === null) {
        return <div className="max-w-6xl mx-auto p-12 flex items-center gap-3 text-gray-600"><Loader2 className="w-5 h-5 animate-spin" /> Lade Anträge…</div>;
    }

    return (
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8" data-testid="admin-pawn-page">
            <Link to="/admin" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-600 hover:text-[#111827] mb-3">
                <ArrowLeft className="w-3.5 h-3.5" /> Admin-Übersicht
            </Link>
            <div className="mb-6">
                <div className="text-xs font-bold uppercase tracking-wider text-[#FFCC00] flex items-center gap-2 mb-1">
                    <Coins className="w-3.5 h-3.5" /> Finanzen
                </div>
                <h1 className="font-display text-3xl md:text-4xl font-black">Pfand &amp; Refinanzierung</h1>
                <p className="text-gray-600 text-sm mt-1">Übersicht aller Pfand- und Refinanzierungs-Anträge mit Status-Workflow.</p>
            </div>

            <div className="mb-4 flex items-center gap-2 flex-wrap">
                <Filter className="w-4 h-4 text-gray-500" />
                <button onClick={() => setFilter("")} className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider border ${filter === "" ? "bg-[#111827] text-white border-[#111827]" : "bg-white border-gray-300 text-gray-700"}`} data-testid="filter-all">
                    Alle ({items.length})
                </button>
                {STATUSES.map((s) => (
                    <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider border ${filter === s ? "bg-[#111827] text-white border-[#111827]" : "bg-white border-gray-300 text-gray-700"}`} data-testid={`filter-${s}`}>
                        {STATUS_LABEL[s]}
                    </button>
                ))}
            </div>

            {items.length === 0 ? (
                <div className="bg-white border border-dashed border-gray-300 rounded-sm p-12 text-center text-gray-500" data-testid="admin-pawn-empty">
                    Keine Anträge gefunden.
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((it) => {
                        const draft = drafts[it.id] || {};
                        const newStatus = draft.status ?? it.status;
                        const newNote = draft.admin_note ?? (it.admin_note || "");
                        const changed = newStatus !== it.status || newNote !== (it.admin_note || "");
                        return (
                            <div key={it.id} className="bg-white border border-gray-200 rounded-sm p-4" data-testid={`pawn-${it.id}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap mb-1">
                                            <h3 className="font-bold text-sm" data-testid={`pawn-${it.id}-vehicle`}>{it.vehicle_label}</h3>
                                            <Badge className={STATUS_CLS[it.status]} data-testid={`pawn-${it.id}-status`}>{STATUS_LABEL[it.status]}</Badge>
                                            <Badge className="bg-gray-100 text-gray-700">{TYPE_LABEL[it.type] || it.type}</Badge>
                                        </div>
                                        <div className="text-xs text-gray-600">
                                            <span className="font-mono-num font-bold">{Number(it.amount_eur).toLocaleString("de-DE")} €</span>
                                            {it.duration_days ? <span> · {it.duration_days} Tage</span> : null}
                                            {it.user_label && <span> · Antragsteller: <strong>{it.user_label}</strong> ({it.user_role})</span>}
                                            <span> · {it.created_at?.slice(0, 16).replace("T", " ")}</span>
                                        </div>
                                        {it.notes && <div className="text-xs italic text-gray-500 mt-1">„{it.notes}"</div>}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-100">
                                    <div>
                                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Neuer Status</label>
                                        <select
                                            value={newStatus}
                                            onChange={(e) => setDrafts((p) => ({ ...p, [it.id]: { ...draft, status: e.target.value } }))}
                                            className="w-full mt-1 rounded-sm border border-gray-300 px-2 py-1.5 text-sm bg-white"
                                            data-testid={`pawn-${it.id}-status-select`}
                                        >
                                            {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                                        </select>
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Admin-Notiz</label>
                                        <Textarea
                                            value={newNote}
                                            onChange={(e) => setDrafts((p) => ({ ...p, [it.id]: { ...draft, admin_note: e.target.value } }))}
                                            rows={2}
                                            className="mt-1 rounded-sm text-xs"
                                            placeholder="Optional — nur intern sichtbar"
                                            data-testid={`pawn-${it.id}-note`}
                                        />
                                    </div>
                                </div>
                                <div className="mt-3 flex justify-end">
                                    <Button
                                        onClick={() => update(it.id, newStatus, newNote || null)}
                                        disabled={!changed || savingId === it.id}
                                        className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider text-xs"
                                        data-testid={`pawn-${it.id}-save`}
                                    >
                                        {savingId === it.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                                        Speichern
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Treuhandkonten Übersicht */}
            <div className="mt-10">
                <h2 className="font-display text-xl font-bold flex items-center gap-2 mb-2"><Landmark className="w-5 h-5 text-[#FFCC00]" /> Treuhandkonten ({escrow.length})</h2>
                {escrow.length === 0 ? (
                    <div className="text-xs text-gray-500 py-4 border border-dashed border-gray-200 rounded-sm text-center">Noch keine Treuhand-Anträge.</div>
                ) : (
                    <div className="space-y-2">
                        {escrow.map((e) => (
                            <div key={e.user_id} className="bg-white border border-gray-200 rounded-sm p-3 flex items-center justify-between" data-testid={`escrow-${e.user_id}`}>
                                <div>
                                    <div className="font-bold text-sm">{e.user_label} <span className="text-xs text-gray-500">({e.user_role})</span></div>
                                    <div className="text-xs text-gray-600 font-mono-num">IBAN: {e.iban || "—"}</div>
                                    {e.note && <div className="text-xs text-gray-500 italic">„{e.note}"</div>}
                                </div>
                                <div className="text-right">
                                    <Badge className={e.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{e.status}</Badge>
                                    <div className="font-mono-num font-bold text-sm mt-1">{Number(e.balance_eur || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
